import React, { useContext, useEffect } from "react";
import {
  Card,
  Col,
  Flex,
  InputNumber,
  Modal,
  Row,
  Space,
  TimePicker,
  Tooltip,
  Upload,
} from "antd";
import { useState } from "react";
import { useRef } from "react";
import Title from "antd/es/typography/Title";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { Form, Input, Typography, Button, Select } from "antd";
import RequiredLabel from "../../../../common/required-label/RequiredLabel";
import TextArea from "antd/es/input/TextArea";
import {
  checkIfFileTypeIsValidForImageAttachment,
  readImageFileAsync,
  sanitizeDataTypeClientSide,
  timeObjToDispViewTimeWithSuffix,
  timeWithSuffixToDispViewTime24hFormat,
} from "../../../../../utils/utils";
import { fileImageUpload } from "../../../../../redux/services/auth/authServices";
import { weekDaysData } from "../../../../../constants/data";
import dayjs from "dayjs";
import {
  createServiceAction,
  getServiceByIdAction,
  updateServiceAction,
} from "../../../../../redux/services/services/servicesServices";
import {
  ArrowLeftOutlined,
  PlusOutlined,
  DeleteOutlined,
} from "@ant-design/icons";
import { baseRouteMaker, routes } from "../../../../../constants/routeConsts";
import { getCategoriesSelectListAction } from "../../../../../redux/services/categories/categoriesServices";
import Actions from "../../../../../redux/actions";
import useUserToken from "../../../../../hooks/useToken";
import { appConfig } from "../../../../../constants/config/appConfig";
import Spinner from "../../../../common/spinner/Spinner";
import { allowCharactersOnlyForDecimalNumbers } from "../../../../../utils/appKeyStrokeLimitingUtils";
import "./Services.scss";
import { toastService } from "../../../../common/toast-messages/toast";
import { current } from "immer";
import { colors } from "../../../../../constants/colors";
import { InfoButtonIcon } from "../../../../../assets/svg";
import { appConstConfig } from "../../../../../constants/config/appConstConfig";
import { LanguageContext } from "../../../../../context/LanguageProvider";

const ServicesForm = () => {
  /* i18n dependencies */
  const { t } = useTranslation();
  /* i18n dependencies */

  /* dispatch an action */
  const dispatch = useDispatch();
  /* dispatch an action */

  /* For navigating between pages */
  const navigate = useNavigate();
  /* For navigating between pages */

  const { selectedLanguage } = useContext(LanguageContext);

  /* loading dependencies */
  const [loading, setLoading] = useState(false);
  const [selectListLoader, setSelectListLoader] = useState(false);
  /* loading dependencies */

  /* form dependencies */
  const [form] = Form.useForm();
  const availability = Form.useWatch("availability", { form, preserve: true });
  const category = Form.useWatch("category", { form, preserve: true });
  /* form dependencies */

  /* id dependencies */
  const { selectedServiceId } = useSelector((state) => state.services);
  /* id dependencies */

  /* select list dependencies */
  const [selectedCategory, setSelectedCategory] = useState(null);
  const { categoriesSelectList, subCategoriesSelectList } = useSelector(
    (state) => state.categories
  );
  /* select list dependencies */

  /* module dependencies for attachment */
  const [openPreviewDialog, setOpenPreviewDialog] = useState(false);
  const [previewImageSrc, setPreviewImageSrc] = useState("");
  const [previewTitle, setPreviewTitle] = useState("");
  const [attachmentList, setAttachmentList] = useState([]);
  const attachmentRef = useRef(null);
  /* module dependencies for attachment */

  /* on Category changed */
  const onCategoryChanged = (id, value) => {
    form.resetFields(["subCategory"]);
    if (value && value !== null && value !== undefined) {
      setSelectedCategory(value);
      dispatch(
        Actions.createAction(Actions.GET_SUB_CATEGORIES_SELECT_LIST, {
          subCategoriesSelectList: value?.subcategories,
        })
      );
    }
  };
  /* on Category changed */

  /* function definition for select list API calls */
  const selectListApiCalls = (details) => {
    // Categories select list
    dispatch(
      getCategoriesSelectListAction(setSelectListLoader, selectedLanguage)
    ).then((apiRes) => {
      const categories = apiRes;
      details &&
        categories &&
        categories?.forEach((element) => {
          if (element._id === details?.categoryInfo?.category?._id) {
            dispatch(
              Actions.createAction(Actions.GET_SUB_CATEGORIES_SELECT_LIST, {
                subCategoriesSelectList: element?.subcategories,
              })
            );
          }
        });
    });
  };
  /* function definition for select list API calls */

  useEffect(() => {
    /* initial dependencies - Edit */
    if (
      selectedServiceId &&
      selectedServiceId !== "" &&
      selectedServiceId !== null &&
      selectedServiceId !== undefined
    ) {
      const dataToBeSent = {
        selectedServiceId,
      };
      dispatch(getServiceByIdAction(dataToBeSent, navigate, setLoading)).then(
        (apiRes) => {
          const service = apiRes;
          const locale = apiRes.locales[0];

          const categoryName =
            selectedLanguage?.value === "ar"
              ? service?.categoryInfo?.category?.locales[0]?.name
              : service?.categoryInfo?.category?.name;

          selectListApiCalls(service);

          form.setFieldsValue({
            title: service?.title,
            title_ar: locale?.title,
            price: service?.price,
            description: service?.description,
            description_ar: locale?.description,
            availability: service?.availability?.map((element) => ({
              ...element,
              startTime: dayjs(
                timeWithSuffixToDispViewTime24hFormat(element?.startTime),
                "HH:mm"
              ),
              endTime: dayjs(
                timeWithSuffixToDispViewTime24hFormat(element?.endTime),
                "HH:mm"
              ),
            })),
            category: categoryName,
            subCategory: service?.categoryInfo?.subcategory?._id,
          });
          setSelectedCategory({
            label: categoryName,
            value: service?.categoryInfo?.category._id,
          });
          setAttachmentList(
            service?.images?.map((element) => ({
              ...element,
              url: element?.url,
            }))
          );
        }
      );
    } else {
      /* initial dependencies - Edit */
      selectListApiCalls(null);
    }
  }, []);

  /* open image preview for dialog */
  const handleAttachmentPreview = async (file) => {
    if (!file.url && !file.preview) {
      file.preview = await readImageFileAsync(file.originFileObj);
    }
    setPreviewImageSrc(file?.url || file?.preview);
    setOpenPreviewDialog(true);
    const tempPreviewTitle =
      file?.name || file?.url?.substring(file?.url?.lastIndexOf("/") + 1);
    setPreviewTitle(tempPreviewTitle);
  };
  /* open image preview for dialog */

  /* Form submit handler */
  const onSubmit = (values) => {
    if (!attachmentList || attachmentList?.length === 0) {
      toastService.error(t("pleaseSelectAtleastOneImage"));
      return;
    }

    const dataToBeSent = {
      ...values,
      availability: values?.availability?.map(({ _id, ...element }) => ({
        ...element,
        startTime: timeObjToDispViewTimeWithSuffix(element?.startTime),
        endTime: timeObjToDispViewTimeWithSuffix(element?.endTime),
      })),
      images: attachmentList?.map((element) => ({
        name: element?.name,
        url: element?.url,
      })),

      categoryInfo: {
        categoryName: selectedCategory?.label,
        category: selectedCategory?.value,
        subcategory: values?.subCategory,
      },
    };

    if (
      selectedServiceId &&
      selectedServiceId !== "" &&
      selectedServiceId !== null &&
      selectedServiceId !== undefined
    ) {
      dataToBeSent._id = selectedServiceId;
      dispatch(updateServiceAction(dataToBeSent, navigate, setLoading));
    } else {
      dispatch(createServiceAction(dataToBeSent, navigate, setLoading));
    }
  };
  /* Form submit handler */

  return (
    <div className="servicesListContainer">
      <Flex vertical gap="2rem">
        <Flex gap="1rem">
          <Button
            type="default"
            shape="circle"
            size="small"
            icon={<ArrowLeftOutlined />}
            onClick={() => navigate(baseRouteMaker(routes.SERVICES))}
          />
          <Title level={2}>{`${
            selectedServiceId
              ? t("Modules.Services.ServicesForm.editService")
              : t("Modules.Services.ServicesForm.addService")
          }`}</Title>
        </Flex>
        {loading ? (
          <Spinner />
        ) : (
          <Row justify="start">
            <Col span={14}>
              <Form
                form={form}
                layout="vertical"
                autoComplete="off"
                requiredMark={false}
                style={{ maxWidth: "500px" }}
                onFinish={onSubmit}
                initialValues={{ availability: [{}] }}
              >
                <Flex vertical gap="0.5rem">
                  <Form.Item
                    label={
                      <RequiredLabel
                        text={t("Modules.Services.ServicesForm.serviceTitle")}
                        required={true}
                      />
                    }
                    name="title"
                    rules={[
                      {
                        required: true,
                        message: t(
                          "Modules.Services.ServicesForm.pleaseEnterServiceTitle"
                        ),
                      },
                      {
                        whitespace: true,
                        message: t(
                          "Modules.Services.ServicesForm.pleaseEnterServiceTitle"
                        ),
                      },
                    ]}
                    normalize={(value) => sanitizeDataTypeClientSide(value)}
                  >
                    <Input
                      placeholder={t(
                        "Modules.Services.ServicesForm.enterServiceTitle"
                      )}
                    />
                  </Form.Item>

                  <Form.Item
                    label={
                      <RequiredLabel
                        text={`${t(
                          "Modules.Services.ServicesForm.serviceTitle"
                        )} ${t("CommonUtils.inArabic")}`}
                        required={true}
                      />
                    }
                    name="title_ar"
                    rules={[
                      {
                        required: true,
                        message: `${t(
                          "Modules.Services.ServicesForm.pleaseEnterServiceTitle"
                        )} ${t("CommonUtils.inArabic")}`,
                      },
                      {
                        whitespace: true,
                        message: `${t(
                          "Modules.Services.ServicesForm.pleaseEnterServiceTitle"
                        )} ${t("CommonUtils.inArabic")}`,
                      },
                    ]}
                    normalize={(value) => sanitizeDataTypeClientSide(value)}
                  >
                    <Input
                      dir="rtl"
                      placeholder={`${t(
                        "Modules.Services.ServicesForm.enterServiceTitle"
                      )} ${t("CommonUtils.inArabic")}`}
                    />
                  </Form.Item>

                  <Form.Item
                    name="category"
                    label={
                      <RequiredLabel
                        text={t("Modules.Services.ServicesForm.category")}
                        required={true}
                      />
                    }
                    rules={[
                      {
                        required: true,
                        message: t(
                          "Modules.Services.ServicesForm.pleaseEnterServiceCategory"
                        ),
                      },
                    ]}
                  >
                    <Select
                      loading={selectListLoader}
                      options={categoriesSelectList}
                      placeholder={t(
                        "Modules.Services.ServicesForm.enterServiceCategory"
                      )}
                      onChange={onCategoryChanged}
                      showSearch
                      filterOption={(input, option) =>
                        (option?.name ?? "")
                          .toLowerCase()
                          .includes(input.toLowerCase())
                      }
                    />
                  </Form.Item>

                  {category && category !== null && category !== undefined ? (
                    <Form.Item
                      name="subCategory"
                      label={
                        <RequiredLabel
                          text={t("Modules.Staff.StaffForm.subCategory")}
                          required={true}
                        />
                      }
                      rules={[
                        {
                          required: true,
                          message: t(
                            "Modules.Staff.StaffForm.pleaseSelectSubCategory"
                          ),
                        },
                      ]}
                    >
                      <Select
                        options={subCategoriesSelectList}
                        placeholder={t(
                          "Modules.Staff.StaffForm.selectSubCategory"
                        )}
                        showSearch
                        filterOption={(input, option) =>
                          (option?.label ?? "")
                            .toLowerCase()
                            .includes(input.toLowerCase())
                        }
                      />
                    </Form.Item>
                  ) : (
                    <></>
                  )}

                  {/* form multiple list - availability */}
                  <Form.List name="availability">
                    {(fields, { add, remove }) => (
                      <>
                        <Form.Item>
                          <Flex justify="space-between">
                            <Title level={3}>{t("addavailability")}</Title>
                            <Button
                              type="primary"
                              shape="circle"
                              size="small"
                              icon={<PlusOutlined />}
                              disabled={fields?.length === 7}
                              onClick={() => add()}
                            />
                          </Flex>
                        </Form.Item>
                        <div
                          style={{
                            display: "flex",
                            rowGap: 16,
                            flexDirection: "column",
                          }}
                        >
                          {fields.map(({ key, name, ...restField }, index) => (
                            <Card
                              size="small"
                              title={
                                <RequiredLabel
                                  text={`Day ${name + 1}`}
                                  required={true}
                                />
                              }
                              key={key}
                              extra={
                                <Button
                                  type="default"
                                  shape="circle"
                                  size="small"
                                  icon={<DeleteOutlined />}
                                  onClick={() => remove(name)}
                                  disabled={fields?.length === 1}
                                />
                              }
                            >
                              <Form.Item
                                style={{ width: "100%" }}
                                {...restField}
                                name={[name, "day"]}
                                rules={[
                                  {
                                    required: true,
                                    message: t(
                                      "Modules.Services.ServicesForm.pleaseSelectDay"
                                    ),
                                  },
                                ]}
                              >
                                <Select
                                  options={weekDaysData.filter(
                                    (element) =>
                                      !availability?.find(
                                        (_element) =>
                                          element?.value === _element?.day
                                      )
                                  )}
                                  placeholder={t(
                                    "Modules.Services.ServicesForm.selectDay"
                                  )}
                                />
                              </Form.Item>

                              <Form.Item
                                {...restField}
                                label={
                                  <RequiredLabel
                                    text={t(
                                      "Modules.Services.ServicesForm.startTime"
                                    )}
                                    required={true}
                                  />
                                }
                                name={[name, "startTime"]}
                                rules={[
                                  {
                                    required: true,
                                    message: t(
                                      "Modules.Services.ServicesForm.pleaseSelectStartTime"
                                    ),
                                  },
                                  {
                                    validator: (_, value) => {
                                      const currMinutes = new Date(
                                        value
                                      ).getMinutes();
                                      if (![0, 30].includes(currMinutes)) {
                                        return Promise.reject(
                                          t(
                                            "CommonUtils.PleaseSelectTimeWithMinutesInStepsOf30"
                                          )
                                        );
                                      }
                                      return Promise.resolve();
                                    },
                                  },
                                ]}
                              >
                                <TimePicker
                                  defaultOpenValue={dayjs('00:00:00', 'HH:mm:ss')}
                                  style={{ width: "100%", cursor: "pointer" }}
                                  use12Hours
                                  format="h:mm a"
                                  onKeyDown={(event) => event.preventDefault()}
                                  disabledDate={(value) => {
                                    const currentTime = new Date(
                                      value
                                    ).getTime();
                                    const allowedStartDate = new Date(
                                      availability[index]?.endTime
                                    ).getTime();
                                    return currentTime > allowedStartDate;
                                  }}
                                  showNow={false}
                                  minuteStep={30}
                                />
                              </Form.Item>

                              <Form.Item
                                {...restField}
                                label={
                                  <RequiredLabel
                                    text={t(
                                      "Modules.Services.ServicesForm.endTime"
                                    )}
                                    required={true}
                                  />
                                }
                                name={[name, "endTime"]}
                                rules={[
                                  {
                                    required: true,
                                    message: t(
                                      "Modules.Services.ServicesForm.pleaseSelectEndTime"
                                    ),
                                  },
                                  {
                                    validator: (_, value) => {
                                      const currMinutes = new Date(
                                        value
                                      ).getMinutes();
                                      if (![0, 30].includes(currMinutes)) {
                                        return Promise.reject(
                                          t(
                                            "CommonUtils.PleaseSelectTimeWithMinutesInStepsOf30"
                                          )
                                        );
                                      }
                                      return Promise.resolve();
                                    },
                                  },
                                ]}
                              >
                                <TimePicker
                                  defaultOpenValue={dayjs('00:00:00', 'HH:mm:ss')}
                                  style={{ width: "100%", cursor: "pointer" }}
                                  use12Hours
                                  format="h:mm a"
                                  onKeyDown={(event) => event.preventDefault()}
                                  disabledDate={(value) => {
                                    const currentTime = new Date(
                                      value
                                    ).getTime();
                                    const allowedEndDate = new Date(
                                      availability[index]?.startTime
                                    ).getTime();
                                    return currentTime < allowedEndDate;
                                  }}
                                  showNow={false}
                                  minuteStep={30}
                                />
                              </Form.Item>
                            </Card>
                          ))}
                        </div>
                      </>
                    )}
                  </Form.List>
                  {/* form multiple list - availability */}

                  <Form.Item
                    label={
                      <RequiredLabel
                        text={t("Modules.Services.ServicesForm.price")}
                        required={true}
                      />
                    }
                    name="price"
                    rules={[
                      {
                        required: true,
                        message: t(
                          "Modules.Services.ServicesForm.pleaseEnterPrice"
                        ),
                      },
                      {
                        validator: (_, value) => {
                          if (value && value < 1)
                            return Promise.reject(
                              `${t("Modules.Services.ServicesForm.price")} ${t(
                                "CommonUtils.shouldBeGreaterThan0"
                              )}`
                            );
                          return Promise.resolve();
                        },
                      },
                    ]}
                  >
                    <Input
                      type="number"
                      placeholder={t(
                        "Modules.Services.ServicesForm.enterPrice"
                      )}
                      onKeyDown={(e) => allowCharactersOnlyForDecimalNumbers(e)}
                    />
                  </Form.Item>

                  <Form.Item
                    label={
                      <RequiredLabel
                        text={t("Modules.Services.ServicesForm.description")}
                        required={true}
                      />
                    }
                    name="description"
                    // normalize={(value) => sanitizeDataTypeClientSide(value)}
                    rules={[
                      {
                        required: true,
                        message: t(
                          "Modules.Services.ServicesForm.pleaseEnterDescription"
                        ),
                      },
                      {
                        whitespace: true,
                        message: t(
                          "Modules.Services.ServicesForm.pleaseEnterDescription"
                        ),
                      },
                    ]}
                  >
                    <TextArea
                      rows={4}
                      showCount
                      maxLength={
                        appConstConfig.DEFAULT_CHARACTERS_ALLOWED_FOR_TEXTAREA
                      }
                      placeholder={t(
                        "CommonUtils.enterDescriptionUpto255Characters"
                      )}
                    />
                  </Form.Item>

                  <Form.Item
                    label={
                      <RequiredLabel
                        text={`${t(
                          "Modules.Services.ServicesForm.description"
                        )} ${t("CommonUtils.inArabic")}`}
                        required={true}
                      />
                    }
                    name="description_ar"
                    // normalize={(value) => sanitizeDataTypeClientSide(value)}
                    rules={[
                      {
                        required: true,
                        message: `${t(
                          "Modules.Services.ServicesForm.pleaseEnterDescription"
                        )} ${t("CommonUtils.inArabic")}`,
                      },
                      {
                        whitespace: true,
                        message: `${t(
                          "Modules.Services.ServicesForm.pleaseEnterDescription"
                        )} ${t("CommonUtils.inArabic")}`,
                      },
                    ]}
                  >
                    <TextArea
                      dir="rtl"
                      rows={4}
                      showCount
                      maxLength={
                        appConstConfig.DEFAULT_CHARACTERS_ALLOWED_FOR_TEXTAREA
                      }
                      placeholder={`${t(
                        "CommonUtils.enterDescriptionUpto255Characters"
                      )} ${t("CommonUtils.inArabic")}`}
                    />
                  </Form.Item>

                  <Button
                    type="primary"
                    htmlType="submit"
                    style={{ width: "100%" }}
                    loading={loading}
                    disabled={loading}
                  >
                    {`${
                      selectedServiceId
                        ? t("Modules.Services.ServicesForm.editService")
                        : t("Modules.Services.ServicesForm.addService")
                    }`}
                  </Button>
                </Flex>
              </Form>
            </Col>
            <Col span={7}>
              <Typography.Title level={4} style={{ marginBottom: 10 }}>
                <Flex gap="0.2rem" align="center">
                  <RequiredLabel text={t("UploadImages")} required={true} />
                  <Tooltip
                    placement="topLeft"
                    title={t("CommonUtils.multipleImagesTooltip")}
                    color={colors.DarkMint}
                  >
                    <Button type="text" shape="circle" size="small">
                      <Flex align="center" justify="center">
                        <InfoButtonIcon />
                      </Flex>
                    </Button>
                  </Tooltip>
                </Flex>
              </Typography.Title>

              <Flex
                style={{ flexDirection: "column-reverse", textAlign: "center" }}
              >
                <Flex vertical gap="0.5rem">
                  <div
                    className="attachmentContainer"
                    style={{ cursor: "pointer" }}
                    onClick={() => attachmentRef.current?.click()}
                  >
                    <Button
                      type="primary"
                      shape="circle"
                      size="middle"
                      icon={<PlusOutlined style={{ fontSize: "22px" }} />}
                    />
                  </div>
                  <Flex
                    vertical
                    style={{ width: "385px" }}
                    align="center"
                    justify="center"
                  >
                    <Typography.Title level={3} type="secondary">
                      {t("CommonUtils.supportedImageFormats")}
                    </Typography.Title>

                    <Typography.Title
                      level={3}
                      type="secondary"
                      style={{ margin: 5 }}
                    >
                      {t("CommonUtils.maxImage")}
                    </Typography.Title>

                    <Typography.Title
                      level={3}
                      type="success"
                      style={{ margin: 0, textAlign: "center" }}
                    >
                      {t("CommonUtils.addImagesInParticular")}
                      <br />
                      {t("CommonUtils.dimension1")}
                      <br />
                      {t("CommonUtils.or")}
                      <br />
                      {t("CommonUtils.dimension2")}
                      <br />
                    </Typography.Title>
                  </Flex>
                </Flex>
                <Flex vertical align="center" justify="center">
                  <Upload
                    listType="picture-card"
                    fileList={attachmentList}
                    multiple
                    accept="image/*"
                    onPreview={handleAttachmentPreview}
                    onChange={(options) => {
                      const tempFileTypeIsValidResArr = options?.fileList?.map((element) => checkIfFileTypeIsValidForImageAttachment(element.name));

                      const isValid = !tempFileTypeIsValidResArr?.some((element) => element.isValid === false);
                      const fileTypeIsValidRes = tempFileTypeIsValidResArr?.find(
                        (element) => element.isValid === false
                      );

                      if (isValid === false) {
                        toastService.error(fileTypeIsValidRes.isInvalidMessage);
                      } else {
                        setAttachmentList(options.fileList);
                      }
                    }}
                    onRemove={(file) =>
                      setAttachmentList(
                        attachmentList.filter(
                          (element) => element.uid !== file.uid
                        )
                      )
                    }
                    customRequest={(options) => {
                      console.log(options,'options');
                      const attachmentFileName = options.file.name;
                      const fileTypeIsValidRes = checkIfFileTypeIsValidForImageAttachment(attachmentFileName);

                      if (fileTypeIsValidRes.isValid === false) {
                        toastService.error(fileTypeIsValidRes.isInvalidMessage);
                        return;
                      } else {
                        const formData = new FormData();
                        formData.append("image", options.file);
                        dispatch(fileImageUpload(formData)).then((apiRes) => {
                          const imageUrl = apiRes?.[0];
                          options.file["url"] = imageUrl;
                          options.onSuccess(imageUrl, options.file);
                        });
                      }
                    }}
                    className="uploadContainer"
                  >
                    <div ref={attachmentRef}></div>
                  </Upload>
                  <Modal
                    open={openPreviewDialog}
                    title={previewTitle}
                    footer={null}
                    onCancel={() => setOpenPreviewDialog(false)}
                  >
                    <img style={{ width: "100%" }} src={previewImageSrc} />
                  </Modal>
                </Flex>
              </Flex>
            </Col>
          </Row>
        )}
      </Flex>
    </div>
  );
};

export default ServicesForm;
